import { CSSObject, SeriesMapOptions, TopoJSON } from 'highcharts/highcharts';
import { MapType } from 'modules/Editor/components/WidgetMenu/SideMenu/MapWidgetMenu/MapWidgetMenu.types';
import { ChangeEvent, KeyboardEvent } from 'react';
import { Color } from 'types/basic.types';
import { WidgetType } from 'types/widget.types';
import { ChartLegendData, ChartWidgetData, FontStyleOptions } from 'widgets/ChartWidgets/ChartWidget.types';

// The strings are mapped to the colorPalette object in sideMenuSettings.json.
export enum ColorPaletteName {
  BLUE = 'blue',
  TEAL = 'teal',
  PURPLE = 'purple',
  RED_PURPLE = 'redPurple',
  YELLOW_RED = 'yellowRed',
  YELLOW_GREEN = 'yellowGreen',
}

export type SeriesMetaColorConfig = {
  colors: string[];
  bubbleColor: string;
  bubbleBackgroundColor: string;
  monochromeBackgroundColor: string;
};

export type ColorPaletteButtonProps = {
  colorPreset: { label: ColorPaletteName; colors: Palette[] };
  colorSteps: number;
  onClick: (colors: string[], paletteColorName: ColorPaletteName) => void;
};

export type LeanChartWidgetData = Pick<
  ChartWidgetData,
  Exclude<keyof ChartWidgetData, 'xAxis' | 'yAxis' | 'grid' | 'patterns' | 'generalOptions' | 'dataLabels'>
>;

type Name = string;
type Key = string;
type Value = string | number;

export type Region = {
  name: Name;
  key: Key;
  value: Value;
};

export type MapChartData = [Key, Value][];

export enum ChoroplethDataFormatType {
  value = 'value',
  category = 'category',
}

export enum ScaleType {
  Steps = 'Steps',
  Gradient = 'Gradient',
}

export interface MapLegendType extends ChartLegendData {
  title: {
    text: string;
    style: FontStyleOptions;
  };
  valueDecimals: number;
  itemStyle: CSSObject;
}

export type CustomDataLabels = {
  enabled: boolean;
  format: string;
  style: CSSObject;
  prefix?: string;
  suffix?: string;
  showName?: boolean;
  showValue?: boolean;
};

export type CustomSeriesMapOptions = Omit<SeriesMapOptions, 'type' | 'data'> & {
  dataLabels: CustomDataLabels;
  nullColor?: Color;
};

type MapWidgetSeriesMeta = {
  colors: string[];
  colorSteps: number;
  // This boolean indicates whether the customize range feature has been turned on or off.
  // It defaults to false.
  isCustomizeRangeEnabled?: boolean;
  dataClasses?: Range[];
  bubbleColor?: string;
  bubbleBackgroundColor?: string;
  monochromeBackgroundColor?: string;
};

export interface MapWidgetData extends LeanChartWidgetData {
  importedMap: TopoJSON;
  legend: MapLegendType;
  series: CustomSeriesMapOptions;
  seriesMeta: MapWidgetSeriesMeta;
  // The name is used to obtain filler colors when seriesMeta.colorSteps increase
  paletteColorName: ColorPaletteName;
  mapType: MapType;
  // Choropleth only: can be gradient colors or step-based colors
  scaleType?: ScaleType;
  // The region name is stored in the widget data, allowing us to obtain it during duplication.
  region?: string;
}

export type DefaultMapWidgetData = {
  widgetType: WidgetType;
  widgetData: MapWidgetData;
};

export type ColorStepsDisplayProps = {
  color: Color;
};

export type Palette = string[] | null;

export type Range = {
  from?: number | undefined;
  to?: number | undefined;
};

export interface RangeInputProps {
  value: string | number;
  onChange: (e: ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  ariaLabel: string;
}
